/**
 * Meta títulos/descripciones alineados con consultas GSC recurrentes (oct–nov 2026).
 * Fuente única para marketplace (ColivingSearchSEO, landing, tests).
 *
 * Reglas de indexación (ColivingSearchSEO):
 * - Solo se aplica en URLs indexables (shouldNoIndex = false).
 * - UK/US: solo rutas EN (/findyourcoliving/...), lang === 'en'.
 */
'use strict';

/** Ciudades ES con queries estables en GSC */
const GSC_ES_CITY_NAMES = new Set(['Madrid', 'Valencia', 'Málaga', 'Bilbao', 'Alicante', 'Barcelona']);

/** NYC + boroughs (GSC: coliving nyc, coliving brooklyn, coliving manhattan…) */
const GSC_US_CITY_NAMES = new Set([
  'New York', 'Brooklyn', 'Manhattan', 'Queens', 'Jersey City', 'Williamsburg', 'Astoria',
]);

/**
 * Formatea un conteo SEO (número o string) a label con separador de miles y "+".
 * 1400 → "1.400+", "350+" → "350+", undefined → fallback.
 */
function fmtCount(raw, fallback, lang) {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'string') return raw.includes('+') ? raw : raw + '+';
  const sep = lang === 'en' ? ',' : '.';
  const str = String(raw).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return str + '+';
}

/**
 * Normaliza un precio eliminando trailing "/mo" para evitar "£800/mo/mo".
 */
function stripTrailingMo(price) {
  if (typeof price !== 'string') return price;
  return price.replace(/\/mo$/i, '').trim();
}

/** Barrios Londres indexables en /findyourcoliving/ (long-tail GSC) */
const GSC_UK_LONDON_AREAS = new Set([
  'Camden', 'Wembley', 'Shoreditch', 'Clapham', 'Canary Wharf', 'Brixton', 'Hackney',
  'Hammersmith', 'Notting Hill', 'Elephant and Castle', 'Kings Cross', 'Bloomsbury',
  'West Hampstead', 'Battersea', 'Islington', 'Greenwich', 'Vauxhall', 'Fulham',
]);

function usCityMeta(city, m) {
  const count = fmtCount(m.citySeoCount, '10+', 'en');
  const min = stripTrailingMo(m.cityMinPrice) || '$1,200';
  const templates = {
    'New York': {
      title: `Coliving NYC 2026 — ${m.nycListingsLabel || count} Furnished Rooms | No Broker Fee | Coolliving`,
      description:
        `Coliving NYC: ${m.nycListings || count}+ furnished rooms in Manhattan, Brooklyn, Queens & Jersey City. Coliving New York from ${min}/mo all-inclusive. No broker fee — book online.`,
      h1: `Coliving NYC — furnished rooms & shared apartments`,
    },
    Brooklyn: {
      title: `Coliving Brooklyn 2026 — ${count} Furnished Rooms | Cheap Coliving NYC | Coolliving`,
      description:
        `Coliving Brooklyn: ${count} furnished rooms in Williamsburg, DUMBO & Park Slope. Coliving in Brooklyn from ${min}/mo, no broker fee. Month-to-month, all-inclusive.`,
      h1: `Coliving Brooklyn — furnished rooms from ${min}/mo`,
    },
    Manhattan: {
      title: `Coliving Manhattan NYC — ${count} Furnished Rooms from ${min}/mo | Coolliving`,
      description:
        `Coliving Manhattan: ${count} furnished coliving rooms in Midtown, Chelsea & UWS. Coliving NYC Manhattan — no broker fee, flexible leases.`,
      h1: `Coliving Manhattan — furnished rooms & prices`,
    },
    Queens: {
      title: `Coliving Queens NYC — Cheap Furnished Rooms from ${min}/mo | Coolliving`,
      description:
        `Coliving Queens: affordable furnished rooms in Astoria & Long Island City. Coliving in Queens NYC from ${min}/mo — no broker fee.`,
      h1: `Coliving Queens — affordable furnished rooms`,
    },
    'Jersey City': {
      title: `Coliving Jersey City — Furnished Rooms from ${min}/mo | PATH to NYC | Coolliving`,
      description:
        `Coliving Jersey City: furnished rooms from ${min}/mo with PATH to Manhattan. Cheaper than NYC coliving — month-to-month, no broker fee.`,
      h1: `Coliving Jersey City — furnished rooms near NYC`,
    },
    Williamsburg: {
      title: `Coliving Williamsburg Brooklyn — Furnished Rooms from ${min}/mo | Coolliving`,
      description:
        `Coliving Williamsburg: furnished rooms in Brooklyn\'s top neighborhood. Coliving Brooklyn from ${min}/mo — L train to Manhattan.`,
      h1: `Coliving Williamsburg — Brooklyn furnished rooms`,
    },
    Astoria: {
      title: `Coliving Astoria Queens — Furnished Rooms from ${min}/mo | Coolliving`,
      description:
        `Coliving Astoria: furnished rooms in Queens from ${min}/mo. Cheap coliving NYC alternative — N/W to Midtown.`,
      h1: `Coliving Astoria — furnished rooms in Queens`,
    },
  };
  return templates[city] || null;
}

function londonAreaMeta(area, m) {
  const count = fmtCount(m.citySeoCount, '5+', 'en');
  const min = stripTrailingMo(m.cityMinPrice) || '£800';
  const roomWord = (count === '1' || count === '1+') ? 'Room' : 'Rooms';
  return {
    title: `Coliving ${area} London 2026 — ${count} Furnished ${roomWord} from ${min}/mo | Coolliving`,
    description:
      `Coliving in ${area}, London: ${count} verified furnished rooms from ${min}/mo all-inclusive. Co-living ${area} — no agency fees. Compare operators and book online.`,
    h1: `Coliving ${area}, London — furnished rooms from ${min}/mo`,
  };
}

/**
 * @param {object} ctx
 * @param {'hub'|'city'|'home'} ctx.pageType
 * @param {'es'|'en'} [ctx.lang]
 * @param {'es'|'uk'|'us'} [ctx.region]
 * @param {string|null} [ctx.city]
 * @param {number|null} [ctx.maxPrice]
 * @param {boolean} [ctx.isStudentMode]
 * @param {object} ctx.metrics
 * @returns {{ title?: string, description?: string, h1?: string }|null}
 */
function getGscSearchMeta(ctx) {
  if (!ctx || ctx.isStudentMode) return null;

  const m = ctx.metrics || {};
  const lang = ctx.lang === 'en' ? 'en' : 'es';
  const city = ctx.city || null;
  const maxPrice = ctx.maxPrice != null ? Number(ctx.maxPrice) : null;

  if (ctx.pageType === 'home') {
    if (lang === 'en') {
      return {
        title: 'Coolliving — Coliving Spain, London & NYC | Verified Rooms',
        description:
          'Coliving marketplace: Spain, co living London and NYC furnished rooms. Compare verified colivings — no tenant booking fees. Coolliving.io',
      };
    }
    return {
      title: 'Coolliving: Coliving España — Madrid, Valencia, Málaga desde 350€/mes',
      description:
        'Coliving y co-living en España: Madrid, Valencia, Málaga, Barcelona. Coliving Madrid, coliving Valencia precios, coliving Málaga. Habitaciones verificadas todo incluido. Sin comisiones.',
      h1: null,
    };
  }

  if (ctx.pageType === 'hub') {
    if (lang === 'en') {
      const london = m.londonListingsLabel || m.londonListings || '3,000+';
      const nyc = m.nycListingsLabel || '1,000+';
      return {
        title: `Coliving London & NYC 2026 — Co-Living London, ${nyc} NYC Rooms | Coolliving`,
        description:
          `Coliving London and co living London: ${london} furnished rooms from £800/mo. Cheapest co living London + NYC coliving (Brooklyn, Manhattan) from $1,200/mo. No broker fee — book online.`,
        h1: `Coliving London & NYC — Co-Living & Furnished Rooms`,
      };
    }
    const rooms = m.roomsEs || '6.900+';
    const min = m.minPriceEs || '350';
    return {
      title: `Coliving España 2026 — ${rooms} Habitaciones | Madrid, Valencia, Málaga desde ${min}€ | Coolliving`,
      description:
        `Coliving en España: ${rooms} habitaciones verificadas. Coliving Madrid, coliving Valencia precios, coliving Málaga y co-living en Barcelona. Flex living todo incluido desde ${min}€/mes. Reserva online.`,
      h1: 'Coliving en España — busca por ciudad y precio',
    };
  }

  if (ctx.region === 'uk' && lang === 'en') {
    if (city === 'London') {
      const n = m.londonListingsLabel || m.londonListings || '3,000+';
      const ops = m.londonOperators || 26;
      return {
        title: `Coliving London 2026 — Co-Living & ${n} Furnished Rooms from £800/mo | Coolliving`,
        description:
          `Coliving London & co living London: ${n} verified furnished rooms. Cheapest co living London from £800/mo all-inclusive. Coliving in London — compare ${ops} operators, no agency fees.`,
        h1: `Co-Living London: ${n} Furnished Rooms from £800/mo`,
      };
    }
    if (city && GSC_UK_LONDON_AREAS.has(city)) {
      return londonAreaMeta(city, m);
    }
  }

  if (ctx.region === 'us' && lang === 'en' && city && GSC_US_CITY_NAMES.has(city)) {
    const block = usCityMeta(city, m);
    if (block) return block;
  }

  if (lang === 'es' && city && GSC_ES_CITY_NAMES.has(city)) {
    if (city === 'Madrid' && maxPrice != null && maxPrice <= 500) {
      return {
        title: 'Coliving Madrid 500 Euros — Habitaciones desde 500€/mes | Coolliving',
        description:
          'Coliving Madrid 500 euros: habitaciones amuebladas todo incluido. Mejores coliving Madrid verificados. Coliving en Madrid flexible — reserva online sin comisiones.',
        h1: 'Coliving Madrid desde 500€/mes',
      };
    }
    if (city === 'Madrid') {
      const count = fmtCount(m.citySeoCount, '1.400+', 'es');
      const min = m.cityMinPrice || '450€';
      return {
        title: `Coliving Madrid 2026: ${count} Habitaciones desde ${min}/mes — Mejores Colivings | Coolliving`,
        description:
          `Coliving Madrid: ${count} habitaciones verificadas desde ${min}/mes. Mejores coliving Madrid, coliving en Madrid y precios actualizados. Todo incluido — sin comisiones.`,
        h1: `Coliving Madrid — ${count} habitaciones y mejores colivings`,
      };
    }
    if (city === 'Valencia') {
      const count = fmtCount(m.citySeoCount, '350+', 'es');
      const min = m.cityMinPrice || '350€';
      return {
        title: `Coliving Valencia 2026 — Precios desde ${min} | Cohousing & Coliving | Coolliving`,
        description:
          `Coliving Valencia precios y cohousing Valencia precios: ${count} habitaciones desde ${min}/mes. Coliving en Valencia, flex living todo incluido. Reserva online.`,
        h1: 'Coliving Valencia — precios y habitaciones verificadas',
      };
    }
    if (city === 'Málaga') {
      const count = fmtCount(m.citySeoCount, '20+', 'es');
      const min = m.cityMinPrice || '400€';
      return {
        title: `Coliving Málaga 2026: ${count} Habitaciones desde ${min}/mes | Coolliving`,
        description:
          `Coliving Málaga: ${count} habitaciones y flex living en la Costa del Sol desde ${min}/mes todo incluido. Coliving en Málaga para nómadas — sin comisiones.`,
        h1: 'Coliving Málaga — habitaciones y precios',
      };
    }
    if (city === 'Bilbao') {
      const count = fmtCount(m.citySeoCount, '15+', 'es');
      const min = m.cityMinPrice || '400€';
      return {
        title: `Coliving Bilbao 2026: ${count} Habitaciones desde ${min} | Coolliving`,
        description:
          `Coliving Bilbao: habitaciones verificadas desde ${min}/mes. Coliving en Bilbao y alquiler flexible todo incluido. Reserva online sin comisiones.`,
        h1: 'Coliving Bilbao — habitaciones amuebladas',
      };
    }
    if (city === 'Alicante') {
      const count = fmtCount(m.citySeoCount, '50+', 'es');
      const min = m.cityMinPrice || '320€';
      return {
        title: `Coliving Alicante 2026: ${count} Habitaciones desde ${min} | Coolliving`,
        description:
          `Coliving Alicante: habitaciones en la Costa Blanca desde ${min}/mes. Coliving en Alicante todo incluido — reserva online.`,
        h1: 'Coliving Alicante — habitaciones verificadas',
      };
    }
    if (city === 'Barcelona') {
      const count = fmtCount(m.citySeoCount, '650+', 'es');
      const min = m.cityMinPrice || '450€';
      return {
        title: `Coliving Barcelona 2026: ${count} Habitaciones desde ${min}/mes | Coolliving`,
        description:
          `Coliving Barcelona: ${count} habitaciones en Gràcia, Eixample y Poblenou desde ${min}/mes. Coliving en Barcelona todo incluido — sin comisiones.`,
        h1: 'Coliving Barcelona — habitaciones verificadas',
      };
    }
  }

  return null;
}

/** Keywords útiles para landing (meta keywords — uso limitado) */
function getGscLandingKeywords() {
  return [
    'coliving españa',
    'coliving madrid',
    'coliving valencia precios',
    'coliving malaga',
    'coliving london',
    'co living london',
    'coliving nyc',
    'coliving brooklyn',
    'coliving manhattan',
    'coolliving',
  ].join(', ');
}

module.exports = {
  getGscSearchMeta,
  getGscLandingKeywords,
  GSC_ES_CITY_NAMES,
  GSC_US_CITY_NAMES,
  GSC_UK_LONDON_AREAS,
};
